/**
 * Created by simon zhao on 9/19/2022.
 * Description:
 * NoteDepositTriggerMetaDialog Component
 * ------ maintenance history ------
 * 10/10/2022 Simon Zhao Changed to use the dedicated template default fields config for note trigger workflow.
 */
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { businessConstants } from '../../../../constants/business.constants';
import { StoreQuerierService } from '../../../../services/store-querier.service';
import { AdhocWorkflowField } from '../../../../tamalelibs/models/adhoc-field.model';
import { AutomationMappingItem, DEFAULT_METDATA_OF_NOTE_TRIGGER } from '../../../../tamalelibs/models/automation.model';
import { ControlType } from '../../../../tamalelibs/models/template-control.model';
import { currentWorkflowAllFieldsSelector } from '../../../../tamalelibs/redux/reducers/workflow.reducer';
import { AlertWindowService } from '../../../../widgets/alert-window/alert-window.service';
import { MultiSelDropdownFilterConfig } from '../../../../widgets/multi-sel-dropdown-filter/multi-sel-dropdown-filter.model';
import { FieldConfig } from '../../field/field.model';
import { NoteDepositMappingItemViewModel, NoteDepositTriggerMetaDialogOptions, NoteDepositTriggerMetaDialogResult } from './note-deposit-trigger-meta-dialog.model';
import { NoteDepositTriggerMetaDialogService } from './note-deposit-trigger-meta-dialog.service';
@Component({
    selector: 'tam-note-deposit-trigger-meta-dialog',
    templateUrl: './note-deposit-trigger-meta-dialog.component.html',
    styleUrls: ['./note-deposit-trigger-meta-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NoteDepositTriggerMetaDialogComponent implements OnInit, OnDestroy {
 //#region public properties
    /**
     * all workflow fields
     */
     allFieldControls: Array<FieldConfig> = [];

     /**
      * the input parameters for current dialog instance.
      */
     dialogOptions: NoteDepositTriggerMetaDialogOptions;

     /**
      * a flag indicating whether current dialog is for note updating.
      */
     get isUpdating(): boolean {
         return this.dialogOptions && this.dialogOptions.isUpdate;
     }

     /**
      * a flag indicating whether the save button is clicked after any change is made.
      */
     isSaveBtnClicked: boolean;

     /**
      * a flag indicating whether the dialog is loading.
      */
     isDialogLoading: boolean;

     /**
      * an array of mapping items.
      */
     mappingMetadataList: Array<NoteDepositMappingItemViewModel> = [];

     /**
      * a flag indicating whether current dialog is visible.
      */
     opened: boolean;

     /**
     * an array of unmapped template fields.
     */
    templateFieldCandidates: Array<NoteDepositMappingItemViewModel> = [];

     /**
      * an array of unmapped template fields.
      */
     workflowFieldCandidates: Array<NoteDepositMappingItemViewModel> = [];

     //#endregion

     //#region private properties
     /**
     * the subscription array that would be unsubscribed on destroying.
     */
     private _destroySubscriptions: Array<Subscription> = [];

     /**
      * the subscription array that would be unsubscribed on closing.
      */
     private _openSubscriptions: Array<Subscription> = [];

     /**
      * a flag indicating whether the dialog content is pristine.
      */
     private _isDialogPristine = true;

     //#endregion

     constructor(private _alertWindow: AlertWindowService,
         private _dialogService: NoteDepositTriggerMetaDialogService,
         private _changeDetectorRef: ChangeDetectorRef,
         private _storeQueriery: StoreQuerierService) { }

     //#region public methods

     @HostListener('window:beforeunload', ['$event'])
     beforeunloadHandler($event: any) {
         if (this.opened) {
             $event.returnValue = true;
         }
     }

     /**
      * a flag indicating whether current configuration is valid.
      */
     get isMappingValid() {
         const isSingleMappingValidFunc = (sItem: NoteDepositMappingItemViewModel) => {
            return !!sItem.fieldInTemplate && !!sItem.fieldsInWorkflow && (!Array.isArray(sItem.fieldsInWorkflow) || Array.from(sItem.fieldsInWorkflow).length > 0);
        };
        const isMultipleMappingValidFunc = (item: NoteDepositMappingItemViewModel) => {
            return !!item.dataSourceForMultipleSelection && !!item.dataSourceForMultipleSelection.data && item.dataSourceForMultipleSelection.data.some(dItem => dItem.checked);
        };
        return this.mappingMetadataList.length === 0 || (this.mappingMetadataList.length > 0 && this.mappingMetadataList.every(it => it.isMultipleSelection ? isMultipleMappingValidFunc(it) : isSingleMappingValidFunc(it)));
     }

     ngOnInit() {
         this._destroySubscriptions.push(
             this._dialogService.dialogOpen$.pipe(filter(action => !this.opened),
             ).subscribe((dialogOptions: NoteDepositTriggerMetaDialogOptions) => {
                 this.dialogOptions = dialogOptions;
                 this._reset();
                 this._initCandidates();
                 this.opened = true;
                 this._changeDetectorRef.detectChanges();
             }));
     }

     ngOnDestroy(): void {
         this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
         this._destroySubscriptions = [];
     }

     /**
      * event handler for the click event of cancel button;
      * @param $event the event argument
      */
     onCancelBtnClick($event: Event) {
         if ($event.stopPropagation) {
             $event.stopPropagation();
         }

         if (!this._isDialogPristine) {
             // pop up the confirmation dialog only when the dialog content has been changed.
             const subscription = this._alertWindow.warn(businessConstants.alertWindow.warnTitle, [businessConstants.alertWindow.warnContent])
                 .subscribe((result: boolean) => {
                     if (result) {
                         this._close();
                     }
                     subscription.unsubscribe();
                 });
         } else {
             this._close();
         }
     }

     /**
      * event handler for the click event of save button.
      * @param e event argument.
      */
     onClickSaveBtn(e: Event) {
         this.isSaveBtnClicked = true;
         if (this.isMappingValid) {
             this._close(this._generateMappingArray());
         }
     }

     /**
      * removes the given mapping item from the list.
      * @param metaItem the abandoned mapping item.
      */
     onDeleteMetaItem(metaItem: NoteDepositMappingItemViewModel) {
         if (metaItem && this.mappingMetadataList.includes(metaItem)) {
             this.mappingMetadataList.splice(this.mappingMetadataList.indexOf(metaItem), 1);
         }
         this._refreshTemplateFieldCandidates();
         this._afterDialogChange();
     }

     /**
      * the event handler for the value change of mapping items.
      * @param e event argument
      */
     onMetaItemFieldsChange(e: any) {
         this._afterDialogChange();
     }

     /**
     * the event handler for the selection change of the candidate list.
     * @param newItem the selected candidate item
     */
    onFieldCandidateSelected(newItem: NoteDepositMappingItemViewModel) {
        const copyOfNewItem = new NoteDepositMappingItemViewModel();
        Object.assign(copyOfNewItem, newItem);
        copyOfNewItem.dataSourceForSingleSelection = this._generateDataSourceForMappingItem(copyOfNewItem.applicableFieldTypes);
        copyOfNewItem.dataSourceForMultipleSelection = this._wrapDataSourceForMultipleSelection(copyOfNewItem.applicableFieldTypes);
        this.mappingMetadataList.push(copyOfNewItem);
        this._refreshTemplateFieldCandidates();
        this._afterDialogChange();
    }

     //#endregion

     //#region private methods
     /**
     * generates a data source list for the given meta data item.
     * @param applicableFieldTypes applicable workflow field types
     * @param isSubmitterIncluded a flag indicating whether the submitters of workflow tasks should be part of the returned list.
     * @param applicableSysVars optional, a key value pair list, each of them represents a system variable, to which the field would map.
     * @returns a list of workflow fields for mapping to the given meta data item.
     */
    private _generateDataSourceForMappingItem(applicableFieldTypes: Array<ControlType>) {
        return this.allFieldControls.filter(fld => applicableFieldTypes.length === 0 || applicableFieldTypes.includes(fld.type)).map(fldCfg => ({ key: fldCfg.label, value: fldCfg.fieldDefinitionId }));
    }

    private _wrapDataSourceForMultipleSelection(applicableFieldTypes: Array<ControlType>) {
        const ds = new MultiSelDropdownFilterConfig();
        const fields = this._generateDataSourceForMappingItem(applicableFieldTypes);
        ds.data = fields.map(f => ({ id: f.value, name: f.key, checked: false }));
        return ds;
    }

     /**
      * generate a new automation record for saving.
      * @returns an automation record for persisting.
      */
     private _generateMappingArray() {
         return this.mappingMetadataList.map(meta => NoteDepositMappingItemViewModel.toAutomationItem(meta));
     }

     private _close(mappingModel: Array<AutomationMappingItem> = null) {
         this.opened = false;
         const dialogResult = new NoteDepositTriggerMetaDialogResult();
         dialogResult.isConfirmed = !!mappingModel;
         if (mappingModel) {
             dialogResult.mappingResult = mappingModel;
         }
         dialogResult.isUpdate = this.dialogOptions.isUpdate;
         this._dialogService.dialogClose$.next(dialogResult);
         this._openSubscriptions.forEach(sub => sub.unsubscribe());
         this._openSubscriptions = [];
         this._changeDetectorRef.detectChanges();
     }

     private _initCandidates() {
         this.allFieldControls = this._storeQueriery.queryBySelector(currentWorkflowAllFieldsSelector);
         if (this.dialogOptions.isUpdate && this.dialogOptions.existingMapping && this.dialogOptions.existingMapping.length > 0) {
            const mappingListRef = [];
            DEFAULT_METDATA_OF_NOTE_TRIGGER.forEach(metaCfg => {
                const item = new NoteDepositMappingItemViewModel();
                item.isDeletable = metaCfg.isDeletable;
                item.isPreloaded = metaCfg.isPreloaded;
                item.isMultipleSelection = metaCfg.isMultipleSelection;
                item.nameOfFieldInTemplate = metaCfg.nameOfFieldInTemplate;
                item.fieldInTemplate = metaCfg.fieldInTemplate;
                item.dataSourceForMultipleSelection = this._wrapDataSourceForMultipleSelection(metaCfg.applicableFieldTypes);
                item.dataSourceForSingleSelection = this._generateDataSourceForMappingItem(metaCfg.applicableFieldTypes);
                mappingListRef.push(item);
            });
            mappingListRef.push(... this._getNewAdhocTemplateFields(this.dialogOptions.noteTypeAdhocFields));
            this.mappingMetadataList = NoteDepositTriggerMetaDialogOptions.toViewModelArray(this.dialogOptions, mappingListRef);
         }
         this._refreshTemplateFieldCandidates();
     }

     private _afterDialogChange() {
         this._isDialogPristine = false;
         this.isSaveBtnClicked = false;
         this._changeDetectorRef.detectChanges();
     }


    /**
     * obtains those adhoc fields available as the candidates for the mapping list.
     * @param adhocFields all adhoc fields associated with current note type
     * @param viewModelList current mapping item list
     * @returns an adhoc canddiate list ready for adding to mapping list.
     */
    private _getNewAdhocTemplateFields(adhocFields: AdhocWorkflowField[], viewModelList?: NoteDepositMappingItemViewModel[]) {
        const adhocList = [];
        if (adhocFields) {
            adhocFields.forEach(adhocField => {
                if (!viewModelList || viewModelList.length === 0 || viewModelList.findIndex(vm => vm.fieldInTemplate === adhocField.attrDefID) < 0 && adhocField.componentType !== 'msd') {
                    const applicableFieldTypes = AdhocWorkflowField.getApplicableControlTypesForNoteTrigger(adhocField);
                    if (applicableFieldTypes && applicableFieldTypes.length > 0) {
                        // some adhoc field type like 'mdl' have no mapping workflow fields so do not put them into the candidate list.
                        const adhocItem = new NoteDepositMappingItemViewModel();
                        adhocItem.isPreloaded = false;
                        adhocItem.isDeletable = true;
                        adhocItem.isMultipleSelection = AdhocWorkflowField.isFieldMultipleValue(adhocField);
                        adhocItem.applicableFieldTypes = applicableFieldTypes;
                        adhocItem.applicableSystemVariables = AdhocWorkflowField.getFieldSystemVariables(adhocField);
                        adhocItem.dataSourceForSingleSelection = this._generateDataSourceForMappingItem(adhocItem.applicableFieldTypes);
                        adhocItem.dataSourceForMultipleSelection = this._wrapDataSourceForMultipleSelection(adhocItem.applicableFieldTypes);
                        adhocItem.fieldInTemplate = adhocField.attrDefID;
                        adhocItem.nameOfFieldInTemplate = adhocField.lableName;
                        adhocList.push(adhocItem);
                    }
                }
            });
        }

        return adhocList;
    }

     private _reset() {
         this.allFieldControls = [];
         this.mappingMetadataList = [];
         this.isSaveBtnClicked = false;
         this._isDialogPristine = true;
     }

     private _refreshTemplateFieldCandidates() {
        const newCandidates = [];
        DEFAULT_METDATA_OF_NOTE_TRIGGER.forEach(metaCfg => {
            // pick up items not preloaded.
            if (!this.mappingMetadataList.find(it => it.fieldInTemplate === metaCfg.fieldInTemplate)) {
                const tF = new NoteDepositMappingItemViewModel();
                Object.assign(tF, metaCfg);
                newCandidates.push(tF);
            }
        });
        newCandidates.push(...this._getNewAdhocTemplateFields(this.dialogOptions.noteTypeAdhocFields, this.mappingMetadataList));
        newCandidates.forEach(candidate => {
            const thisRef = this;
            candidate.click = item => {
                thisRef.onFieldCandidateSelected(item);
            };
        });
        this.templateFieldCandidates = newCandidates;
        this._changeDetectorRef.detectChanges();
    }

     //#endregion
}
